import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import GenerateAudio from "../../components/screens/GenerateAudio/GenerateAudioScreen";
import config from "../../config";
import useServices from "../../hooks/useServices";
import useAudioPlayer from "../../hooks/useAudioPlayer";
import { users as userQuerys } from "@nualang/nualang-api-and-queries/Queries";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import { useNavigate } from "react-router";
import { useApp } from "../../hooks";

import logger from "../../logger";

const { languages, internalDomains } = config;

function GenerateAudioWrapper() {
  const { t } = useTranslation();
  const [tts] = useServices();
  const { addToQueue } = useAudioPlayer();
  const [audioUrl, setAudioUrl] = useState(null);
  const { user, openSnackbar } = useApp();
  const { username } = user;
  const navigate = useNavigate();

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, false);
      return response;
    },
    {
      userId: username,
    },
    {
      enabled: !!username,
    },
  );

  const currentUser =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const isLoading = userQuery.isLoading || userQuery.isFetching;

  useEffect(() => {
    if (
      !isLoading &&
      !internalDomains.some((domain) =>
        currentUser.UserAttributes?.email?.endsWith(domain),
      )
    ) {
      navigate("/classrooms");
    }
  }, [isLoading, currentUser, navigate]);

  const [generateTxt, setGenerateTxt] = useState(false);

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        languageCode,
        voiceSpeed,
        pitch,
      } = params;

      const result = await tts(
        text,
        learnLanguage,
        voiceName,
        languageCode,
        voiceSpeed,
        pitch,
      );

      if (!mute && result && result.audioUrl) {
        addToQueue(result.audioUrl);
        setAudioUrl(result.audioUrl);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const downloadAudio = async (params) => {
    if (!audioUrl) return;

    try {
      const { voiceName, language, text } = params;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `audio_${voiceName}_${language}_${timestamp}.mp3`;
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setAudioUrl(null);
      if (generateTxt) {
        const fileNameWithoutExtension = fileName.replace(".mp3", "");
        const txtContent = `${text}\n`;
        const blob = new Blob([txtContent], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileNameWithoutExtension}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      logger.error(`Failed to download audio: ${error}`);
    }
  };

  if (isLoading) {
    return <p>{t("loading")}</p>;
  }

  return (
    <div>
      {internalDomains.some((domain) =>
        currentUser.UserAttributes?.email?.endsWith(domain),
      ) ? (
        <GenerateAudio
          t={t}
          languages={languages}
          handleSpeak={handleSpeak}
          audioUrl={audioUrl}
          downloadAudio={downloadAudio}
          openSnackbar={openSnackbar}
          generateTxt={generateTxt}
          setGenerateTxt={setGenerateTxt}
        />
      ) : (
        (<p>{t("access_denied_domain_unverified")}</p>) // Optional message for non-verified users
      )}
    </div>
  );
}

export default GenerateAudioWrapper;
